import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui';
import { Icon } from './icons';
import { getThemeColors } from '../lib/theme';
import { v2Users, type V1User } from '../lib/v1';
import { wiedervorlageState, wiedervorlageHighlight, type WiedervorlageState } from '../pages/pflege/wiedervorlage';
import {
  wiedervorlageHistory, markWiedervorlageErledigt,
  updateWiedervorlageEntry, deleteWiedervorlageEntry, markWiedervorlageEntryErledigt,
  type WiedervorlageEntry,
} from '../lib/wiedervorlage';
import { DateTimeInput, splitDateTime } from './DateTimeInput';
import { formatDate } from '../lib/dateFormat';
import { toBerlinDateKey } from '../lib/localDate';

/** Wiedervorlage gibt es für BK-Anfragen (user_role=1) und PB-Anfragen (user_role=2). */
export function isWiedervorlageRole(role: unknown): boolean {
  const r = Number(role);
  return r === 1 || r === 2;
}

/**
 * Batch-13 / Paket D — Wiedervorlage: Datum + optionale Notiz je Anfrage
 * (PB- und BK-User), mit Historie (Zähler "n/N") und automatischem Beenden nach der
 * N-ten überfälligen Wiedervorlage (Backend-Command wiedervorlage:auto-close).
 * Eigener Save-Pfad (PATCH /v2/admin/users/{id}/wiedervorlage), unabhängig
 * vom "Schnell bearbeiten"-Save — mirrors PhaseRulesBlock.
 *
 * Fälligkeit wird farbig markiert (s. wiedervorlage.ts): erreicht = gelb,
 * überschritten = rot. „Wiedervorlage löschen" entfernt nur den Spiegel auf
 * `users` (die Notiz zieht serverseitig mit, das Datum ist das führende
 * Feld) — die Historie bleibt unangetastet und zählt weiter zum Limit N.
 * Ist die Historie bei N angekommen, sperrt der "Wiedervorlage speichern"-
 * Button das SETZEN eines neuen Termins (Löschen bleibt möglich).
 *
 * Datum wird überall als reiner "Y-m-d"-String behandelt, nie über
 * `new Date(...)` geführt (Bug B3 — TZ-Shift hätte das angezeigte Datum um
 * einen Tag verschieben können).
 */
export function WiedervorlageBlock({
  dark,
  raw,
  onSaved,
}: {
  dark: boolean;
  raw: V1User | null;
  onSaved: (u: V1User) => void;
}) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const [am, setAm] = useState('');
  const [notiz, setNotiz] = useState('');
  // Befund 23.09.: Uhrzeit, Default "00:00" (= keine Uhrzeit/ganztägig).
  // `raw` (users-Spiegel) trägt NIE eine Uhrzeit (v1-kompatibel, bleibt
  // Datum-only) — der tatsächliche Wert kommt ausschließlich aus der
  // Historie (loadHistory()/refreshAfterEntryChange()), sobald sie geladen
  // ist; bis dahin steht das Feld auf dem Default.
  const [zeit, setZeit] = useState('00:00');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [history, setHistory] = useState<WiedervorlageEntry[]>([]);
  const [max, setMax] = useState<number>(5);
  const [count, setCount] = useState<number>(0);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [erledigtSaving, setErledigtSaving] = useState(false);
  // editingEntryId !== null → das Formular oben bearbeitet einen konkreten
  // Verlaufs-Eintrag (Klick auf eine Zeile im "Verlauf") statt die aktuelle
  // ("offene") Wiedervorlage des Users. Speichern/Löschen/Erledigt markieren
  // wirken dann per PATCH/DELETE /v2/admin/wiedervorlagen/{id} auf GENAU
  // diese Zeile.
  const [editingEntryId, setEditingEntryId] = useState<number | null>(null);
  const editingEntry = history.find(h => h.id === editingEntryId) ?? null;

  useEffect(() => {
    // Eingabefelder gehören einer NEUEN Wiedervorlage und bleiben leer; die
    // bestehende offene Wiedervorlage wird nur angezeigt (Zusammenfassung).
    setAm('');
    setNotiz('');
    setZeit('00:00');
    setEditingEntryId(null);
  }, [raw?.id, raw?.wiedervorlage_am, raw?.wiedervorlage_notiz]);

  // Rückmeldung ("Gespeichert") nur beim Wechsel des Users zurücksetzen, nicht
  // bei jedem Spiegel-Update nach dem eigenen Speichern.
  useEffect(() => {
    setSaved(false);
    setError(null);
  }, [raw?.id]);

  const currentAm = raw?.wiedervorlage_am ? toBerlinDateKey(String(raw.wiedervorlage_am)) : '';
  const userId = raw?.id;
  const isSupported = !!userId && isWiedervorlageRole(raw?.user_role);

  const loadHistory = React.useCallback(async () => {
    if (!userId) return;
    try {
      const h = await wiedervorlageHistory(userId);
      setHistory(h.data);
      setMax(h.max);
      setCount(h.count);
      return h;
    } catch {
      // Historie ist eine Zusatzinfo — ein fehlgeschlagener Abruf darf das
      // Setzen/Ändern der Wiedervorlage selbst nicht blockieren.
      return null;
    }
  }, [userId]);

  useEffect(() => {
    if (isSupported) void loadHistory();
  }, [isSupported, loadHistory]);

  /**
   * Nach einer Historien-Änderung (bearbeiten/löschen/erledigt via
   * /v2/admin/wiedervorlagen/{id}): Verlauf neu laden UND die Formularfelder
   * + `raw` auf den serverseitig neu berechneten Spiegel (letzte offene
   * Zeile) ziehen — die Antworten dieser Endpunkte tragen nur den Eintrag,
   * nicht den User, daher wird der Spiegel hier aus der frischen Liste
   * abgeleitet (identische Regel wie WiedervorlageController::
   * refreshMirrorFromLatestOpen).
   */
  const refreshAfterEntryChange = async () => {
    const h = await loadHistory();
    if (!h || !raw) return;
    const latestOpen = h.data.find(e => e.erledigt_am === null) ?? null;
    const nextAm = latestOpen?.faellig_am ?? '';
    const nextNotiz = latestOpen?.notiz ?? '';
    setAm('');
    setNotiz('');
    setZeit('00:00');
    onSaved({ ...raw, wiedervorlage_am: nextAm || null, wiedervorlage_notiz: nextNotiz || null } as V1User);
  };

  /** Bestehenden Verlaufs-Eintrag ins Formular laden. */
  const startEditEntry = (entry: WiedervorlageEntry) => {
    setEditingEntryId(entry.id);
    setAm(entry.faellig_am);
    setNotiz(entry.notiz ?? '');
    setZeit(entry.faellig_um ?? '00:00');
    setSaved(false);
    setError(null);
  };

  /** Zurück zur „aktuellen" Wiedervorlage (Formular wieder auf den Spiegel). */
  const cancelEditEntry = () => {
    setEditingEntryId(null);
    setAm('');
    setNotiz('');
    setZeit('00:00');
    setError(null);
  };

  // Nur PB-Anfragen (user_role=2, AnfragenPage) und BK-Anfragen (user_role=1,
  // BkAnfragenPage) — für andere Rollen lehnt das Backend mit 404 ab.
  if (!raw?.id || !isSupported) return null;

  // Markierung aus dem aktuell im Feld stehenden Datum ableiten, nicht aus
  // `raw`: so springt die Farbe sofort mit, sobald der Nutzer das Datum
  // ändert — auch schon vor dem Speichern.
  const state: WiedervorlageState = wiedervorlageState(am || currentAm);
  const tone = wiedervorlageHighlight(state, dark) ?? null;

  // Ein neuer Termin (am !== '') legt serverseitig eine neue Historien-Zeile
  // an — ist das Limit N erreicht, lehnt der Server das mit 422 ab. Der
  // Button wird hier bereits vorab gesperrt, damit der Klick gar nicht erst
  // zu einem Fehler führt. Löschen (am === '') bleibt immer möglich.
  // Gilt nur fürs Anlegen einer NEUEN Zeile — das Bearbeiten eines
  // bestehenden Verlaufs-Eintrags (editingEntry) legt keine neue Zeile an
  // und zählt daher nicht gegen das Limit.
  const limitReached = count >= max;
  const wouldExceedLimit = !editingEntry && limitReached && am !== '';

  const openEntry = history.find(h => h.erledigt_am === null) ?? null;
  // Im Bearbeiten-Modus: „Erledigt markieren" nur, wenn der bearbeitete
  // Eintrag noch offen ist. Sonst (Normalmodus): wie bisher an openEntry.
  const canMarkErledigt = editingEntry ? editingEntry.erledigt_am === null : !!openEntry;

  /** Setzt/ändert die AKTUELLE (offene) Wiedervorlage des Users. */
  const save = async (nextAm: string, nextNotiz: string, nextZeit: string = '00:00') => {
    setSaving(true);
    setError(null);
    try {
      const result = await v2Users.setWiedervorlage(raw.id, {
        wiedervorlage_am: nextAm || null,
        wiedervorlage_notiz: nextAm ? (nextNotiz || null) : null,
        // Backend normalisiert "00:00"/leer zu NULL (= ganztägig) — Frontend
        // muss das nicht vorab unterscheiden.
        wiedervorlage_um: nextAm ? (nextZeit || '00:00') : null,
      });
      onSaved({ ...raw, wiedervorlage_am: result.wiedervorlage_am, wiedervorlage_notiz: result.wiedervorlage_notiz });
      setAm('');
      setNotiz('');
      // Felder bleiben nach dem Speichern leer (nächste, neue Wiedervorlage).
      setZeit('00:00');
      setSaved(true);
      void loadHistory();
    } catch (e) {
      const err = e as { response?: { data?: { errors?: Array<{ code?: string }> } } };
      const code = err?.response?.data?.errors?.[0]?.code;
      setError(
        code === 'E_WIEDERVORLAGE_MAX'
          ? tr('anfragen.wiedervorlage.max_reached', { max })
          : (e instanceof Error ? e.message : tr('pflege.edit.save_failed')),
      );
    } finally {
      setSaving(false);
    }
  };

  /**
   * Formular-Speichern: im Bearbeiten-Modus (Klick auf einen Verlaufs-
   * Eintrag) PATCH auf GENAU diese Zeile, sonst wie bisher die aktuelle
   * Wiedervorlage setzen/ändern.
   */
  const submitForm = async () => {
    if (!editingEntry) return save(am, notiz, zeit);
    setSaving(true);
    setError(null);
    try {
      await updateWiedervorlageEntry(editingEntry.id, { faellig_am: am, notiz: notiz || null, faellig_um: zeit || '00:00' });
      setSaved(true);
      setEditingEntryId(null);
      await refreshAfterEntryChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : tr('pflege.edit.save_failed'));
    } finally {
      setSaving(false);
    }
  };

  /**
   * „Löschen": im Bearbeiten-Modus löscht dies GENAU den ausgewählten
   * Verlaufs-Eintrag (inkl. verknüpfter Notiz/Kalendertermin, serverseitig),
   * sonst wie bisher nur den Spiegel auf `users` leeren.
   */
  const clearOrDeleteEntry = async () => {
    if (!editingEntry) return save('', '');
    setSaving(true);
    setError(null);
    try {
      await deleteWiedervorlageEntry(editingEntry.id);
      setEditingEntryId(null);
      await refreshAfterEntryChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : tr('benutzer.error.delete'));
    } finally {
      setSaving(false);
    }
  };

  /**
   * „Erledigt markieren": im Bearbeiten-Modus GENAU der ausgewählte
   * Eintrag, sonst wie bisher die zuletzt offene Zeile des Users.
   */
  const markErledigt = async () => {
    setErledigtSaving(true);
    setError(null);
    try {
      if (editingEntry) {
        await markWiedervorlageEntryErledigt(editingEntry.id);
        setEditingEntryId(null);
        await refreshAfterEntryChange();
      } else {
        await markWiedervorlageErledigt(raw.id);
        setAm('');
        setNotiz('');
        setZeit('00:00');
        const h = await loadHistory();
        if (h) {
          const latestOpen = h.data.find(e => e.erledigt_am === null) ?? null;
          onSaved({ ...raw, wiedervorlage_am: latestOpen?.faellig_am ?? null, wiedervorlage_notiz: latestOpen?.notiz ?? null } as V1User);
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : tr('pflege.detail.wiedervorlage_mark_done_error'));
    } finally {
      setErledigtSaving(false);
    }
  };

  return (
    <section
      data-wiedervorlage-state={state}
      style={tone ? {
        background: tone.bg, border: `1px solid ${tone.border}`,
        borderRadius: 10, padding: '12px 14px',
      } : undefined}
    >
      <div style={{
        fontSize: 11, fontWeight: 600, color: t.muted, textTransform: 'uppercase',
        letterSpacing: '0.04em', marginBottom: 10,
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
      }}>
        {tr('anfragen.wiedervorlage.title') || 'Wiedervorlage'}
        <span
          data-testid="wiedervorlage-counter"
          title={tr('anfragen.wiedervorlage.counter_hint') || 'Anzahl bisheriger Wiedervorlagen / Maximum'}
          style={{
            textTransform: 'none', letterSpacing: 0, fontSize: 11, fontWeight: 700,
            color: limitReached ? '#b42318' : t.muted,
            background: t.cardMuted, border: `1px solid ${t.border}`,
            borderRadius: 9999, padding: '1px 8px',
          }}
        >
          {count}/{max}
        </span>
        {tone && (
          <span style={{
            textTransform: 'none', letterSpacing: 0, fontSize: 11, fontWeight: 700,
            color: tone.text, background: tone.badgeBg, border: `1px solid ${tone.border}`,
            borderRadius: 9999, padding: '1px 8px',
          }}>
            {tr(tone.labelKey)}
          </span>
        )}
      </div>
      {/* Bestehende offene Wiedervorlage: nur anzeigen, nicht ins Formular vorbefüllen. */}
      {!editingEntry && currentAm && (
        <div
          data-testid="wiedervorlage-current"
          style={{ fontSize: 12, color: t.text, marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <span style={{ fontWeight: 600 }}>
            {tr('anfragen.wiedervorlage.current') || 'Aktuelle Wiedervorlage'}: {formatDate(currentAm, currentAm)}
            {openEntry?.faellig_um ? ` ${openEntry.faellig_um}` : ''}
          </span>
          {raw.wiedervorlage_notiz && <span style={{ color: t.muted }}>{raw.wiedervorlage_notiz}</span>}
          {openEntry && (
            <div>
              <Button variant="ghost" size="sm" dark={dark} onClick={() => startEditEntry(openEntry)}>
                {tr('anfragen.wiedervorlage.edit_current') || 'Ändern'}
              </Button>
            </div>
          )}
          <span style={{ color: t.muted }}>
            {tr('anfragen.wiedervorlage.current_hint') || 'Die Felder unten gelten für eine neue Wiedervorlage. Zum Ändern „Ändern“ oder einen Eintrag im Verlauf anklicken.'}
          </span>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
        {/* Datum + Uhrzeit in einem Feld; 00:00 = ganztägig (Befund 23.09.). */}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, color: t.muted }}>
          {tr('anfragen.wiedervorlage.datetime')}
          <DateTimeInput
            ariaLabel={tr('anfragen.wiedervorlage.datetime')}
            date={am}
            time={zeit}
            commitOnChange
            onCommit={v => {
              const { date, time } = splitDateTime(v, { midnightIsAllDay: false });
              setAm(date ?? '');
              setZeit(time ?? '00:00');
              setSaved(false);
            }}
            style={{ border: `1px solid ${t.border}`, borderRadius: 8, padding: '8px 10px', background: t.inputBg, color: t.text, fontFamily: 'Outfit,sans-serif', fontSize: 13 }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, color: t.muted }}>
          {tr('anfragen.wiedervorlage.note') || 'Notiz'}
          <input
            aria-label={tr('anfragen.wiedervorlage.note') || 'Wiedervorlage-Notiz'}
            type="text"
            value={notiz}
            disabled={!am}
            onChange={e => { setNotiz(e.target.value); setSaved(false); }}
            placeholder={am ? '' : (tr('anfragen.wiedervorlage.note_needs_date') || 'zuerst Datum setzen')}
            style={{ border: `1px solid ${t.border}`, borderRadius: 8, padding: '8px 10px', background: am ? t.inputBg : t.cardMuted, color: t.text, fontFamily: 'Outfit,sans-serif', fontSize: 13 }}
          />
        </label>
      </div>
      {editingEntry && (
        <div style={{ fontSize: 11, color: t.muted, marginBottom: 6 }}>
          {(tr('anfragen.wiedervorlage.editing_entry', { nr: editingEntry.nr })
            || `Bearbeitet Verlaufs-Eintrag #${editingEntry.nr}`)}
        </div>
      )}
      <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Button
          variant="secondary" size="sm" dark={dark}
          onClick={() => void submitForm()}
          disabled={saving || wouldExceedLimit}
        >
          {saving ? tr('settings.wiedervorlage_max.saving') : (tr('anfragen.wiedervorlage.save') || 'Wiedervorlage speichern')}
        </Button>
        {(editingEntry || raw.wiedervorlage_am || am) && (
          <Button variant="ghost" size="sm" dark={dark} onClick={() => void clearOrDeleteEntry()} disabled={saving}>
            {tr('anfragen.wiedervorlage.clear') || 'Wiedervorlage löschen'}
          </Button>
        )}
        {canMarkErledigt && (
          <Button variant="ghost" size="sm" dark={dark} onClick={() => void markErledigt()} disabled={erledigtSaving}>
            {erledigtSaving
              ? tr('settings.wiedervorlage_max.saving')
              : (tr('anfragen.wiedervorlage.mark_done') || 'Als erledigt markieren')}
          </Button>
        )}
        {editingEntry && (
          <Button variant="ghost" size="sm" dark={dark} onClick={cancelEditEntry} disabled={saving}>
            {tr('common.action.cancel') || 'Abbrechen'}
          </Button>
        )}
        {saved && <span style={{ fontSize: 12, color: '#039855', fontWeight: 600 }}>{tr('settings.wiedervorlage_max.saved')}</span>}
        {error && <span style={{ fontSize: 12, color: '#b42318', fontWeight: 600 }}>{error}</span>}
      </div>
      {wouldExceedLimit && (
        <div style={{ marginTop: 6, fontSize: 12, color: '#b42318', fontWeight: 600 }}>
          {tr('anfragen.wiedervorlage.max_reached', { max }) || `Maximale Anzahl an Wiedervorlagen (${max}) erreicht.`}
        </div>
      )}
      {history.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <button
            type="button"
            onClick={() => setHistoryOpen(o => !o)}
            aria-expanded={historyOpen}
            style={{
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              fontSize: 12, fontWeight: 600, color: t.muted, display: 'flex', alignItems: 'center', gap: 4,
            }}
          >
            <Icon.Chevron dir={historyOpen ? 'down' : 'right'} c={t.muted} />
            {(tr('anfragen.wiedervorlage.history') || 'Verlauf')} ({history.length})
          </button>
          {historyOpen && (
            <ul style={{ listStyle: 'none', margin: '8px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {/* Jeder Eintrag ist klickbar — lädt Datum+Notiz oben ins
                  Formular; Speichern/Löschen/Erledigt markieren wirken dann
                  auf GENAU diesen Eintrag (s. editingEntryId). */}
              {history.map(h => (
                <li
                  key={h.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => startEditEntry(h)}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); startEditEntry(h); } }}
                  style={{
                    fontSize: 12, color: t.text, background: t.cardMuted,
                    border: `1px solid ${editingEntryId === h.id ? '#7f56d9' : t.border}`, borderRadius: 8, padding: '6px 10px',
                    display: 'flex', flexDirection: 'column', gap: 2, cursor: 'pointer',
                  }}
                >
                  <span style={{ fontWeight: 600 }}>
                    #{h.nr} · {formatDate(h.faellig_am, h.faellig_am)}{h.faellig_um ? ` ${h.faellig_um}` : ''}
                    {h.erledigt_am
                      ? `, ${tr('anfragen.wiedervorlage.done_on') || 'erledigt am'} ${formatDate(h.erledigt_am, h.erledigt_am)}`
                      : `, ${tr('anfragen.wiedervorlage.open') || 'offen'}`}
                  </span>
                  {h.notiz && <span style={{ color: t.muted }}>{h.notiz}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
